<template>
  <div class="font-semibold">
    <p class="mb-4">
      <span class="text-red-100">**</span>
      {{ AMENITIES_TEXT }}
    </p>
    <div v-if="editable">
      <!-- Housing Level Amenities -->
      <div
        data-testid="editing"
        v-for="{ id, title } in housingLevelAmenities"
        :key="id"
        class="flex justify-between w-fit items-center mb-3"
      >
        <input
          :id="id"
          :checked="stateCheckbox(id)"
          type="checkbox"
          class="h-4 w-4 form-checkbox border-gray-300 mr-10"
          @input="updateHousingAmenitiesValue(id, $event)"
        />
        <label :for="id">{{ title }}</label>
        <div v-if="stateCheckbox(id)" class="ml-16">
          <label :for="`price_${id}`" class="mr-2">Price: $</label>
          <input
            :id="`price_${id}`"
            type="text"
            :value="defaultPriceInput(id)"
            class="form-input"
            maxlength="10"
            @input="updatePriceInput(id, $event.target.value)"
          />
        </div>
      </div>

      <!-- Meal Plan Section -->
      <div
        class="flex justify-left w-fit items-center flex-wrap mb-3 sm:justify-between"
      >
        <input
          id="meal_plan"
          :checked="openMealPlanSelect"
          type="checkbox"
          class="h-4 w-4 form-checkbox border-gray-300 mr-10"
          @input="closeMealPlanCheckBox($event)"
        />
        <label for="meal_plan" class="mr-5">Meal Plan</label>
        <v-select
          v-if="openMealPlanSelect"
          id="meal_plan_select"
          :model-value="mealPlanDefaultSelected"
          :options="mealPlanAmenities"
          label="title"
          item-value="id"
          class="styled-v-select w-72 xs:w-382px font-normal"
          @update:modelValue="changeSelectedMealPlan($event)"
        ></v-select>
        <div v-if="openMealPlanSelect" class="ml-16">
          <label class="mr-2">Price: $</label>
          <input
            type="text"
            :value="defaultPriceInput(selectedMealPlan)"
            :disabled="!selectedMealPlan"
            class="form-input"
            :class="{
              'hover:cursor-not-allowed bg-blue-100': !selectedMealPlan,
            }"
            maxlength="10"
            @input="updatePriceInputMealPlan($event.target.value)"
          />
        </div>
      </div>

      <!-- None Applicable Checkbox -->
      <div class="flex justify-between w-fit items-center">
        <input
          id="none"
          type="checkbox"
          :checked="isCurrentEditingAmenitiesEmpty"
          class="h-4 w-4 form-checkbox border-gray-300 mr-10"
          @input="clearCurrentAmenities($event)"
        />
        <label for="none">None applicable</label>
      </div>
    </div>

    <!-- View Mode -->
    <div v-else>
      <!-- Display Selected Amenities with Prices -->
      <div
        v-for="amenity in currentEditingHousingAmenities"
        :key="amenity.amenities_id"
        class="flex justify-between w-fit items-center"
      >
        <template v-if="!isMealPlanAmenity(amenity) && amenity.title">
          <span>{{ amenity.title + "&nbsp" }}</span>
          <span
            v-if="(price = defaultPriceInput(amenity.amenities_id))"
            class="font-normal"
            >{{ "$" + price }}</span
          >
        </template>
      </div>

      <!-- Display Selected Meal Plan -->
      <div v-if="mealPlanCheckBox">
        <div class="flex items-center mb-3">
          <span> {{ "Meal Plan&nbsp" }} </span>
          <span class="font-normal">
            {{ mealPlanDefaultSelected.title + "&nbsp" }}</span
          >
          <span
            v-if="(price = defaultPriceInput(selectedMealPlan))"
            class="font-normal"
            >${{ price }}</span
          >
        </div>
      </div>

      <!-- Display "None applicable" if no amenities are selected -->
      <div v-if="isCurrentEditingAmenitiesEmpty && !hasSelectedAmenities">
        <span>None applicable</span>
      </div>
    </div>
    <p class="font-semibold my-4">
      {{ ACCESSIBILITY_TEXT }}
    </p>
    <div v-if="editable">
      <div
        data-testid="editing"
        v-for="{ title, id } in accessibilityAmenities"
        :key="id"
        class="flex justify-between w-fit items-center mb-3"
      >
        <input
          :id="id"
          :checked="stateCheckbox(id)"
          type="checkbox"
          class="h-4 w-4 form-checkbox border-gray-300 mr-10"
          @input="updateHousingAmenitiesValue(id, $event)"
        />
        <label :for="id">{{ title }}</label>
      </div>
    </div>
    <div v-else>
      <p class="font-normal">
        {{ selectedAccessibilityFeatures.join(", ") }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, ref } from "vue";
import { useStore } from "vuex";

import { AMENITIES_CATEGORIES, AMENITIES_GRANULARITIES } from "@/constants";

const provideEdit = inject("provideEdit");
const props = defineProps({ amenities: { type: Object, required: true } });

const editable = computed(() => provideEdit());

const ACCESSIBILITY_TEXT =
  "Select all the accessibility & inclusivity information that applies to this building/housing option";
const AMENITIES_TEXT = "Select all the building Amenities that applies to this building/housing options";

const store = useStore();

const housingLevelAmenities = computed(() =>
  props.amenities.filterAmenities(AMENITIES_CATEGORIES.AMENITIES, AMENITIES_GRANULARITIES.HOUSING_LEVEL),
);

const accessibilityAmenities = computed(() =>
  props.amenities.filterAmenities(
    AMENITIES_CATEGORIES.ACCESSIBILITY_AND_INCLUSIVITY,
    AMENITIES_GRANULARITIES.HOUSING_LEVEL,
  ),
);
const mealPlanAmenities = computed(() =>
  props.amenities.filterAmenities(AMENITIES_CATEGORIES.MEAL_PLAN, AMENITIES_GRANULARITIES.HOUSING_LEVEL),
);

const currentEditingHousingAmenities = computed(
  () => store.getters["programManager/getCurrentEditingHousingLevelAmenities"],
);

// A meal plan row read from the API always carries its category (the service
// hydrates it from the amenity join). Rows attached in this session are stamped
// here, so they stay identifiable before save even if the catalog request is
// still in flight or failed. The catalog lookup covers neither case on its own..
const isMealPlanAmenity = (amenity) =>
  amenity?.category === AMENITIES_CATEGORIES.MEAL_PLAN ||
  mealPlanAmenities.value.some(({ id }) => id === amenity?.amenities_id);

const withoutMealPlanAmenities = (amenities) => amenities.filter((el) => !isMealPlanAmenity(el));

const isCurrentEditingAmenitiesEmpty = computed(
  () => !(currentEditingHousingAmenities.value.length || openMealPlanSelect.value),
);

const stateCheckbox = (id) => {
  return currentEditingHousingAmenities.value.some((el) => el.amenities_id === id);
};

const selectedAccessibilityFeatures = computed(
  () =>
    accessibilityAmenities.value?.filter((amenity) => stateCheckbox(amenity.id))?.map((amenity) => amenity.title) ??
    [],
);

const formatDecimalNumber = (newVal) => {
  const formatedValue = `${newVal}`.padStart(4, "0");
  return newVal ? formatedValue.replace(/([0-9]{2})$/g, ".$1") : null;
};

const defaultPriceInput = (id) => {
  return formatDecimalNumber(currentEditingHousingAmenities.value.find((el) => el.amenities_id === id)?.cost);
};

const mealPlanDefaultSelected = computed(() =>
  mealPlanAmenities.value.find(({ id }) => currentEditingHousingAmenities.value.some((el) => el.amenities_id === id)),
);
const selectedMealPlan = computed(() => mealPlanDefaultSelected.value?.id);

const mealPlanCheckBox = computed(() => !!mealPlanDefaultSelected.value);

// The section is open either because a meal plan is already attached, or because
// the user ticked the checkbox and has not chosen a type yet.
const userOpenedMealPlan = ref(false);
const openMealPlanSelect = computed(() => mealPlanCheckBox.value || userOpenedMealPlan.value);

// Re-picking the option already selected is not a change, and rebuilding the row
// would drop the price entered for it. Switching to a different type does clear
// the price: it belongs to the plan it was entered for.
const changeSelectedMealPlan = (value) => {
  if (value?.id === selectedMealPlan.value) {
    return;
  }
  const payload = [
    ...withoutMealPlanAmenities(currentEditingHousingAmenities.value),
    ...(value?.id ? [{ amenities_id: value.id, category: AMENITIES_CATEGORIES.MEAL_PLAN }] : []),
  ];
  store.commit("programManager/setCurrentEditingHousingAmenities", payload);
};

const closeMealPlanCheckBox = ($event) => {
  userOpenedMealPlan.value = $event.target.checked;
  if (!$event.target.checked) {
    store.commit(
      "programManager/setCurrentEditingHousingAmenities",
      withoutMealPlanAmenities(currentEditingHousingAmenities.value),
    );
  }
};

const updatePriceInput = (amenities_id, value) => {
  const cost = value ? parseInt(`0${value.replace(/[\D]+/g, "")}`) : null;
  const payload = currentEditingHousingAmenities.value.map((el) =>
    el.amenities_id === amenities_id ? { ...el, cost } : el,
  );
  store.commit("programManager/setCurrentEditingHousingAmenities", payload);
};

const updatePriceInputMealPlan = (cost) => {
  const amenities_id = selectedMealPlan.value;
  updatePriceInput(amenities_id, cost);
};

const updateHousingAmenitiesValue = (id, event) => {
  const payload = event.target.checked
    ? [...currentEditingHousingAmenities.value, { amenities_id: id }]
    : currentEditingHousingAmenities.value.filter((el) => el.amenities_id !== id);
  store.commit("programManager/setCurrentEditingHousingAmenities", payload);
};
const clearCurrentAmenities = (event) => {
  if (event.target.checked) {
    const filteredAmenities = currentEditingHousingAmenities.value.filter(
      (el) => !housingLevelAmenities.value.some(({ id }) => id === el.amenities_id),
    );
    store.commit("programManager/setCurrentEditingHousingAmenities", filteredAmenities);
  }
};
</script>
<style>
#meal_plan_select .vs__dropdown-option {
  width: max-content;
}
</style>
